import { useState, useRef, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Terminal, Mail, Lock, Eye, EyeOff, Shield,
  RefreshCw, CheckCircle, AlertCircle, ArrowRight, UserPlus,
  LogIn, RotateCcw, KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// ── Constants ────────────────────────────────────────────────────────────────
const ADMIN_EMAIL = "jp-empire@eay.jp";
const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;
const SUPABASE_FN_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || "bbygnqkxijbplzuhktfm"}.supabase.co/functions/v1`;

// ── Helpers ──────────────────────────────────────────────────────────────────
function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local[0] || "u"}***@${domain}`;
}

const rateLimitMap = new Map<string, number[]>();
function checkRateLimit(key: string, maxAttempts: number, windowMs: number): boolean {
  const now = Date.now();
  const attempts = (rateLimitMap.get(key) || []).filter((t) => now - t < windowMs);
  if (attempts.length >= maxAttempts) return false;
  rateLimitMap.set(key, [...attempts, now]);
  return true;
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const inputCls = "w-full bg-muted/30 border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all";
const btnPrimary = "w-full py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold transition-all hover:shadow-neon disabled:opacity-50 flex items-center justify-center gap-2";
const btnOutline = "w-full py-2.5 rounded-lg border border-border/50 bg-muted/20 text-muted-foreground text-sm hover:bg-muted/40 transition-all flex items-center justify-center gap-2";
const btnDanger = "w-full py-2.5 rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-sm hover:bg-destructive/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50";

// ── Sub-components ────────────────────────────────────────────────────────────
function PasswordStrength({ password }: { password: string }) {
  const checks: [string, boolean][] = [
    ["6+ characters", password.length >= 6],
    ["Uppercase letter", /[A-Z]/.test(password)],
    ["Number", /[0-9]/.test(password)],
  ];
  const passed = checks.filter(([, ok]) => ok).length;
  const color = passed === 3 ? "text-primary" : passed >= 2 ? "text-secondary" : "text-muted-foreground";
  return (
    <ul className={`text-xs space-y-0.5 pl-1 ${color}`}>
      {checks.map(([label, ok]) => (
        <li key={label} className="flex items-center gap-1.5">
          <span>{ok ? "✅" : "○"}</span> {label}
        </li>
      ))}
    </ul>
  );
}

function OtpInput({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const handleChange = (i: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const arr = value.padEnd(OTP_LENGTH, " ").split("");
    arr[i] = digit || " ";
    const next = arr.join("").trimEnd();
    onChange(next);
    if (digit && i < OTP_LENGTH - 1) inputs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!value[i] || value[i] === " ") { if (i > 0) inputs.current[i - 1]?.focus(); }
      else { const arr = value.padEnd(OTP_LENGTH, " ").split(""); arr[i] = " "; onChange(arr.join("").trimEnd()); }
    } else if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < OTP_LENGTH - 1) inputs.current[i + 1]?.focus();
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(pasted);
    inputs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };
  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={value[i] && value[i] !== " " ? value[i] : ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-11 h-14 text-center text-xl font-bold bg-muted/30 border border-border/50 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all disabled:opacity-50 caret-transparent"
        />
      ))}
    </div>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />
      <div className="flex-1 flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-neon">
          <Terminal className="w-6 h-6 text-primary-foreground" />
        </div>
        <div className="text-left">
          <h1 className="text-xl font-bold text-gradient-cyber inline-block">JP AI Dorks</h1>
          <p className="text-xs text-muted-foreground">Enterprise OSINT Platform</p>
        </div>
      </div>
    </div>
  );
}

// ── Stage types ───────────────────────────────────────────────────────────────
type Stage =
  | "email"          // Step 1: enter email
  | "admin-set-pw"   // Admin: set/change password (no password prompt first)
  | "signin"         // Regular user: sign in with existing password
  | "create"         // Regular user: create new account
  | "otp-verify"     // Regular user: verify OTP after sign-up
  | "forgot-sent";   // Regular user: password reset email sent

// ── Main component ────────────────────────────────────────────────────────────
const Auth = () => {
  const navigate = useNavigate();
  const { user, isLoading, signIn } = useAuth();

  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Admin set-password state
  const [adminSecret, setAdminSecret] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showSecretField, setShowSecretField] = useState(false);

  // OTP state
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return c - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;

  // ── Step 1: Email Continue ─────────────────────────────────────────────────
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    // Admin goes to normal sign-in — password prompt first, bootstrap reset link available if needed
    if (trimmed === ADMIN_EMAIL.toLowerCase()) {
      setEmail(ADMIN_EMAIL); // normalize
      setStage("signin");
      return;
    }

    setCheckingEmail(true);
    try {
      await fetch(`${SUPABASE_FN_URL}/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch {
      // Do not block auth flow if telemetry endpoint fails
    } finally {
      setCheckingEmail(false);
      setEmail(trimmed);
      setPassword("");
      setConfirmPassword("");
      setStage("signin");
    }
  };

  // ── Admin: Set Password & Sign In ─────────────────────────────────────────
  // Strategy: call admin-set-password edge fn (service role sets pw + confirms email),
  // then immediately signInWithPassword — no URL hash, no OTP, no dead-end possible.
  const handleAdminSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Password too short", description: "Minimum 6 characters.", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (!adminSecret.trim()) {
      toast({ title: "Bootstrap secret required", description: "Enter your ADMIN_BOOTSTRAP_SECRET to proceed.", variant: "destructive" });
      setShowSecretField(true);
      return;
    }

    setSubmitting(true);
    try {
      // 1. Call edge function to set password via service role
      const res = await fetch(`${SUPABASE_FN_URL}/admin-set-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-bootstrap-secret": adminSecret.trim(),
        },
        body: JSON.stringify({ new_password: password }),
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // 2. Immediately sign in with the new password — guaranteed to work
      await signIn(ADMIN_EMAIL, password);
      toast({ title: "✅ Admin access granted", description: "Welcome, JP Admin." });
      navigate("/admin");
    } catch (err: any) {
      const msg = err.message || "Please try again.";
      if (msg.toLowerCase().includes("forbidden") || msg.toLowerCase().includes("403")) {
        toast({ title: "Invalid secret", description: "Check your ADMIN_BOOTSTRAP_SECRET and try again.", variant: "destructive" });
        setShowSecretField(true);
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Admin: Reset Account (wipe + recreate) ─────────────────────────────────
  const handleAdminReset = async () => {
    if (!adminSecret.trim()) {
      toast({ title: "Secret required", description: "Enter your ADMIN_REPAIR_SECRET.", variant: "destructive" });
      setShowSecretField(true);
      return;
    }
    if (!window.confirm("This will delete and recreate the admin account. Are you sure?")) return;

    setResetLoading(true);
    try {
      const res = await fetch(`${SUPABASE_FN_URL}/admin-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-reset-secret": adminSecret.trim(),
        },
        body: JSON.stringify({ redirectTo: `${window.location.origin}/auth` }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast({ title: "✅ Admin account reset", description: "Now set a new password below." });
      setPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast({ title: "Reset failed", description: err.message, variant: "destructive" });
    } finally {
      setResetLoading(false);
    }
  };

  // ── Regular: Sign In ──────────────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    if (!checkRateLimit(`signin:${email}`, 6, 60_000)) {
      toast({ title: "Too many attempts", description: "Please wait before trying again.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      toast({ title: "Welcome back!" });
    } catch (err: any) {
      const msg = (err?.message || "").toLowerCase();
      if (msg.includes("email not confirmed")) {
        toast({ title: "Email not verified", description: "Check your inbox for a confirmation code.", variant: "destructive" });
      } else {
        toast({ title: "Sign in failed", description: "Invalid password. Try Forgot Password if needed.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Regular: Create Account ───────────────────────────────────────────────
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Password too short", description: "Minimum 6 characters.", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (!checkRateLimit(`signup:${email}`, 3, 300_000)) {
      toast({ title: "Too many attempts", description: "Please wait before trying again.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { display_name: email.split("@")[0] } },
      });
      if (error) {
        if (error.message.toLowerCase().includes("already registered")) {
          toast({ title: "Account already exists", description: "Please sign in instead." });
          setStage("signin");
          return;
        }
        throw error;
      }
      if (data.user && !data.user.email_confirmed_at) {
        toast({ title: "Check your email", description: `6-digit code sent to ${maskEmail(email.trim())}` });
        setStage("otp-verify");
        startCooldown();
      } else {
        toast({ title: "Account created!", description: "You are now signed in." });
      }
    } catch (err: any) {
      toast({ title: "Sign up failed", description: err.message || "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Regular: Forgot Password ──────────────────────────────────────────────
  const handleForgotPassword = async () => {
    if (!checkRateLimit(`forgot:${email}`, 3, 300_000)) {
      toast({ title: "Too many requests", variant: "destructive" }); return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/set-password`,
      });
      if (error) throw error;
      setStage("forgot-sent");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  // ── Regular: OTP ──────────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    const code = otpCode.replace(/\s/g, "");
    if (code.length !== OTP_LENGTH) { setOtpError("Please enter the full 6-digit code."); return; }
    if (!checkRateLimit(`verify:${email}`, 5, 300_000)) { setOtpError("Too many attempts. Request a new code."); return; }
    setVerifying(true); setOtpError("");
    try {
      const { error } = await supabase.auth.verifyOtp({ email: email.trim(), token: code, type: "signup" });
      if (error) {
        setOtpError(error.message.toLowerCase().includes("expired")
          ? "Code has expired. Request a new one."
          : "Invalid code. Please check and try again.");
        return;
      }
      toast({ title: "✅ Email verified!", description: "You are now signed in." });
    } catch { setOtpError("Verification failed. Please try again."); }
    finally { setVerifying(false); }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    if (!checkRateLimit(`resend:${email}`, 3, 300_000)) {
      toast({ title: "Too many resend requests", variant: "destructive" }); return;
    }
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email: email.trim() });
      if (error) throw error;
      setOtpCode(""); setOtpError(""); startCooldown();
      toast({ title: "Code resent", description: `Check ${maskEmail(email.trim())}` });
    } catch { toast({ title: "Failed to resend", variant: "destructive" }); }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════════════════

  // ── OTP verify screen ─────────────────────────────────────────────────────
  if (stage === "otp-verify") {
    return (
      <PageShell>
        <Logo />
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">Verify your email</h2>
          <p className="text-muted-foreground text-sm mt-2">
            We sent a 6-digit code to <span className="text-foreground font-medium">{maskEmail(email)}</span>
          </p>
          <p className="text-muted-foreground text-xs mt-1">Code expires in 5 minutes</p>
        </div>
        <div className="glass-card p-6 space-y-6">
          <OtpInput value={otpCode} onChange={setOtpCode} disabled={verifying} />
          {otpError && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /><span>{otpError}</span>
            </div>
          )}
          <button onClick={handleVerifyOtp} disabled={verifying || otpCode.replace(/\s/g, "").length !== OTP_LENGTH} className={btnPrimary}>
            {verifying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {verifying ? "Verifying…" : "Verify & Sign In"}
          </button>
          <div className="text-center space-y-2">
            <button onClick={handleResendOtp} disabled={resendCooldown > 0} className="text-sm text-primary hover:underline disabled:opacity-50 flex items-center gap-1 mx-auto">
              <RefreshCw className="w-3 h-3" />
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
            <button onClick={() => { setStage("email"); setOtpCode(""); setOtpError(""); }} className="text-xs text-muted-foreground hover:text-foreground">
              ← Start over
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Forgot-sent screen ────────────────────────────────────────────────────
  if (stage === "forgot-sent") {
    return (
      <PageShell>
        <Logo />
        <div className="glass-card p-8 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-primary mx-auto" />
          <h2 className="text-xl font-bold text-foreground">Password reset sent</h2>
          <p className="text-muted-foreground text-sm">
            Check your inbox at <span className="text-foreground font-medium">{maskEmail(email)}</span> for a reset link.
          </p>
          <button onClick={() => setStage("signin")} className={btnOutline}>← Back to sign in</button>
        </div>
      </PageShell>
    );
  }

  // ── Admin set-password screen ─────────────────────────────────────────────
  if (stage === "admin-set-pw") {
    return (
      <PageShell>
        <Logo />
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">Set Admin Password</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Setting password for <span className="text-foreground font-medium">{ADMIN_EMAIL}</span>
          </p>
          <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full text-xs bg-primary/20 text-primary border border-primary/30">
            <Shield className="w-3 h-3" /> Administrator
          </span>
        </div>

        <form onSubmit={handleAdminSetPassword} className="glass-card p-6 space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-2 text-xs bg-primary/10 border border-primary/20 rounded-lg px-3 py-2.5 text-primary">
            <KeyRound className="w-4 h-4 shrink-0 mt-0.5" />
            <span>No previous password required. Enter a new password and your bootstrap secret to set it immediately.</span>
          </div>

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">New password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type={showPw ? "text" : "password"}
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className={`${inputCls} pl-10 pr-12 py-3`}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Confirm password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type={showPw ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                className={`${inputCls} pl-10 pr-4 py-3`}
              />
            </div>
          </div>

          {/* Strength + match */}
          {password && <PasswordStrength password={password} />}
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Passwords don't match
            </p>
          )}

          {/* Bootstrap secret — always visible for admin flow */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">
              Bootstrap secret <span className="text-xs text-muted-foreground/60">(ADMIN_BOOTSTRAP_SECRET)</span>
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="password"
                required
                value={adminSecret}
                onChange={(e) => setAdminSecret(e.target.value)}
                placeholder="Paste your secret here"
                className={`${inputCls} pl-10 pr-4 py-3 font-mono text-sm`}
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || password.length < 6 || password !== confirmPassword || !adminSecret.trim()}
            className={btnPrimary}
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {submitting ? "Setting password & signing in…" : "Set Password & Sign In"}
          </button>

          {/* Divider */}
          <div className="relative flex items-center gap-2 py-1">
            <div className="flex-1 border-t border-border/30" />
            <span className="text-xs text-muted-foreground">Advanced</span>
            <div className="flex-1 border-t border-border/30" />
          </div>

          {/* Reset button */}
          <button
            type="button"
            onClick={handleAdminReset}
            disabled={resetLoading || !adminSecret.trim()}
            className={btnDanger}
          >
            {resetLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            {resetLoading ? "Resetting account…" : "Reset Admin Account (wipe & recreate)"}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            Wipes the admin account and recreates it fresh. Then set a new password above.
          </p>

          {/* Back */}
          <button type="button" onClick={() => { setStage("email"); setPassword(""); setConfirmPassword(""); setAdminSecret(""); }} className={btnOutline}>
            ← Use a different email
          </button>
        </form>

        <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground/70">
          <Shield className="w-4 h-4 mt-0.5 shrink-0" />
          <p>Admin access is server-enforced. No email OTP required for this account.</p>
        </div>
      </PageShell>
    );
  }

  // ── Standard flow (email / signin / create) ───────────────────────────────
  return (
    <PageShell>
      <Logo />

      <div className="text-center mb-6">
        {stage === "email" && (
          <>
            <h2 className="text-2xl font-bold text-foreground">Welcome</h2>
            <p className="text-muted-foreground text-sm mt-1">Enter your email to continue</p>
          </>
        )}
        {stage === "signin" && (
          <>
            <h2 className="text-2xl font-bold text-foreground">Continue securely</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Enter your password or create a new account without revealing whether <span className="text-foreground font-medium">{email}</span> is already registered.
            </p>
          </>
        )}
        {stage === "create" && (
          <>
            <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
            <p className="text-muted-foreground text-sm mt-1">
              New account for <span className="text-foreground font-medium">{email}</span>
            </p>
          </>
        )}
      </div>

      <div className="glass-card p-6 space-y-4">

        {/* Step 1: Email */}
        {stage === "email" && (
          <form onSubmit={handleEmailContinue} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${inputCls} pl-10 pr-4 py-3`}
                />
              </div>
            </div>
            <button type="submit" disabled={checkingEmail || !email.trim()} className={btnPrimary}>
              {checkingEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {checkingEmail ? "Checking…" : "Continue"}
            </button>
          </form>
        )}

        {/* Step 2a: Sign In (existing user) */}
        {stage === "signin" && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-muted-foreground">Password</label>
                {email === ADMIN_EMAIL ? (
                  <button type="button" onClick={() => setStage("admin-set-pw")} className="text-xs text-primary hover:underline">
                    Set/reset password (admin)
                  </button>
                ) : (
                  <button type="button" onClick={handleForgotPassword} className="text-xs text-primary hover:underline">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className={`${inputCls} pl-10 pr-12 py-3`}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={submitting} className={btnPrimary}>
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {submitting ? "Signing in…" : "Sign In"}
            </button>
            {email !== ADMIN_EMAIL && (
              <button type="button" onClick={() => { setPassword(""); setConfirmPassword(""); setStage("create"); }} className={btnOutline}>
                <UserPlus className="w-4 h-4" />
                Create a new account
              </button>
            )}
            <button type="button" onClick={() => { setStage("email"); setPassword(""); }} className={btnOutline}>
              ← Use a different email
            </button>
          </form>
        )}

        {/* Step 2b: Create Account (new user) */}
        {stage === "create" && (
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Create password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className={`${inputCls} pl-10 pr-12 py-3`}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  className={`${inputCls} pl-10 pr-4 py-3`}
                />
              </div>
            </div>
            {password && <PasswordStrength password={password} />}
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Passwords don't match
              </p>
            )}
            <button type="submit" disabled={submitting || password.length < 6 || password !== confirmPassword} className={btnPrimary}>
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {submitting ? "Creating account…" : "Create Account & Sign In"}
            </button>
            <button type="button" onClick={() => { setStage("email"); setPassword(""); setConfirmPassword(""); }} className={btnOutline}>
              ← Use a different email
            </button>
          </form>
        )}
      </div>

      <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground/70">
        <Shield className="w-4 h-4 mt-0.5 shrink-0" />
        <p>For educational, research, and security testing purposes only. Unauthorized use is prohibited.</p>
      </div>
    </PageShell>
  );
};

export default Auth;
